import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { Menu } from '@headlessui/react';
import {
  EllipsisVerticalIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import {
  EyeIcon,
  PencilSquareIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { TbFilterPause } from "react-icons/tb";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import sessionCheck from "../session/userData";
import { encryptDataHybrid } from "../enc/useEncrypt";
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getDeviceInfo } from '../utils/deviceInfo';
import { motion, AnimatePresence } from 'framer-motion';
import { Switch } from '@headlessui/react';
import Swal from 'sweetalert2';

const API_URL = process.env.REACT_APP_API_URL;

const Plans = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [access_data, setAccess_data] = useState([]);

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Modal and edit states
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedPlan, setEditedPlan] = useState(null);
  const [isAddNew, setIsAddNew] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    type: ''
  });

  const statusOptions = ['All', 'Active', 'Inactive'];
  const typeOptions = ['All', 'default', 'custom'];

  useEffect(() => {
    if (sessionCheck() == null) {
      navigate("../login", { replace: true });
    } else {
      setAccess_data(JSON.parse(sessionCheck()));
    }
  }, []);

  useEffect(() => {
    if (access_data?.username) {
      fetchPlans();
    }
  }, [access_data?.username]);

  useEffect(() => {
    if (filterDrawerOpen) {
      // disable scrolling
      document.body.style.overflow = "hidden";
    } else {
      // enable scrolling again
      document.body.style.overflow = "auto";
    }

    // cleanup when component unmounts
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [filterDrawerOpen]);

  const fetchPlans = async (filterParams = null) => {
    setLoading(true);

    try {
      const activeFilters = filterParams || filters;

      const dataToEncrypt = {
        user_id: access_data?.username || '',
        login_id: access_data?.login_id || '',
        device_id: getDeviceInfo().deviceId,
        payload: {
          query: 'fetch-plans',
          search: activeFilters.search,
          status: activeFilters.status === 'Active' ? '1' : activeFilters.status === 'Inactive' ? '0' : '',
          type: activeFilters.type === 'All' ? '' : activeFilters.type
        }
      };

      const encryptedMessage = encryptDataHybrid(dataToEncrypt);

      if (!encryptedMessage) {
        toast.error("Encryption failed. Please try again.");
        setLoading(false);
        return;
      }

      const payloadToSend = {
        encrypted_key: encryptedMessage.encryptedKey,
        encrypted_data: encryptedMessage.encryptedData,
      };

      const response = await fetch(`${API_URL}plans.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
        },
        body: JSON.stringify(payloadToSend),
      });

      const result = await response.json();

      if (result.status === true) {
        setPlans(result.data);

        // Update pagination
        setPagination(prev => ({
          ...prev,
          currentPage: 1,
          totalItems: result.data.length,
          totalPages: Math.ceil(result.data.length / prev.itemsPerPage)
        }));
      } else {
        throw new Error(result.message || "Failed to fetch plan data");
      }
    } catch (error) {
      console.error("Fetch plans error:", error);
      toast.error(error.message || "An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (planId, newStatus) => {
    const originalStatus = plans.find(plan => plan.plan_id === planId)?.status;
    const statusText = newStatus ? '1' : '0';

    try {
      // Optimistic UI update
      setPlans(plans.map(plan =>
        plan.plan_id === planId ? { ...plan, status: statusText } : plan
      ));

      const response = await updatePlanStatus(planId, statusText);

      if (response?.status !== true) {
        throw new Error(response?.message || 'Status update failed');
      }

      toast.success(`Status updated successfully`);
    } catch (error) {
      // Revert to original status on failure
      setPlans(plans.map(plan =>
        plan.plan_id === planId ? { ...plan, status: originalStatus } : plan
      ));

      toast.error(error.message || 'Failed to update status');
    }
  };

  const updatePlanStatus = async (planId, newStatus) => {
    try {
      const dataToEncrypt = {
        user_id: access_data?.username || '',
        login_id: access_data?.login_id || '',
        device_id: getDeviceInfo().deviceId,
        payload: {
          query: 'status-update',
          plan_id: planId,
          newStatus: newStatus
        }
      };

      console.log('Data to encrypt for status update:', dataToEncrypt);

      const encryptedMessage = encryptDataHybrid(dataToEncrypt);
      if (!encryptedMessage) {
        throw new Error("Encryption failed");
      }

      const response = await fetch(`${API_URL}plan-operation.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
        },
        body: JSON.stringify({
          encrypted_key: encryptedMessage.encryptedKey,
          encrypted_data: encryptedMessage.encryptedData,
        }),
      });

      const result = await response.json();

      if (result.status !== true) {
        throw new Error(result.message || "Update rejected by server");
      }

      return result;
    } catch (error) {
      console.error('Status update error:', error);
      throw error;
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = () => {
    setFilterDrawerOpen(false);
    fetchPlans();
  };

  const resetFilters = () => {
    const defaultFilters = {
      search: '',
      status: '',
      type: ''
    };
    setFilters(defaultFilters);
    fetchPlans(defaultFilters);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  const getPaginatedData = () => {
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;
    return plans.slice(startIndex, endIndex);
  };

  const toggleMobileSidebar = () => {
    setMobileSidebarOpen(!mobileSidebarOpen);
  };

  // Modal functions
  const openPlanModal = (planId, isNew = false) => {
    if (isNew) {
      setIsAddNew(true);
      setEditedPlan({
        plan_id: '',
        plan_name: '',
        status: '1',
        type: 'custom'
      });
    } else {
      const plan = plans.find(p => p.plan_id === planId);
      if (plan) {
        setSelectedPlan(plan);
        setEditedPlan({ ...plan });
        setIsAddNew(false);
      }
    }
    setIsPlanModalOpen(true);
    setIsEditing(true);
  };

  const closePlanModal = () => {
    setIsPlanModalOpen(false);
    setSelectedPlan(null);
    setIsEditing(false);
    setIsAddNew(false);
  };

  const handleSavePlan = async () => {
    try {
      const dataToEncrypt = {
        user_id: access_data?.username || '',
        login_id: access_data?.login_id || '',
        device_id: getDeviceInfo().deviceId,
        payload: {
          query: isAddNew ? 'add-plan' : 'update-plan',
          plan_id: editedPlan.plan_id,
          plan_name: editedPlan.plan_name,
          status: editedPlan.status,
        }
      };

      console.log('Data to encrypt for save:', dataToEncrypt);

      const encryptedMessage = encryptDataHybrid(dataToEncrypt);

      if (!encryptedMessage) {
        toast.error("Encryption failed. Please try again.");
        return;
      }

      const payloadToSend = {
        encrypted_key: encryptedMessage.encryptedKey,
        encrypted_data: encryptedMessage.encryptedData,
      };

      const response = await fetch(`${API_URL}plan-operation.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
        },
        body: JSON.stringify(payloadToSend),
      });

      const result = await response.json();

      if (result.status === true) {
        toast.success(isAddNew ? "Plan added successfully" : "Plan updated successfully");
        fetchPlans();
        closePlanModal();
      } else {
        toast.error(result.message || "Failed to save plan");
      }
    } catch (error) {
      console.error("Save plan error:", error);
      toast.error("An unexpected error occurred. Please try again.");
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedPlan(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    if (isPlanModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isPlanModalOpen]);

  const deletePlan = async (planId) => {
    const confirm = await Swal.fire({
      title: 'Are you sure?',
      text: "Do you really want to delete this plan?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    });

    if (!confirm.isConfirmed) return;

    try {
      const dataToEncrypt = {
        user_id: access_data?.username || '',
        login_id: access_data?.login_id || '',
        device_id: getDeviceInfo().deviceId,
        payload: {
          query: 'delete-plan',
          plan_id: planId, // use planId from argument
        }
      };

      console.log('Data to encrypt for delete:', dataToEncrypt);

      const encryptedMessage = encryptDataHybrid(dataToEncrypt);

      if (!encryptedMessage) {
        toast.error("Encryption failed. Please try again.");
        return;
      }

      const payloadToSend = {
        encrypted_key: encryptedMessage.encryptedKey,
        encrypted_data: encryptedMessage.encryptedData,
      };

      const response = await fetch(`${API_URL}plan-operation.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
        },
        body: JSON.stringify(payloadToSend),
      });

      const result = await response.json();

      if (result.status === true) {
        toast.success("Plan deleted successfully");
        fetchPlans();
        closePlanModal();
      } else {
        toast.error(result.message || "Failed to delete plan");
      }
    } catch (error) {
      console.error("Delete plan error:", error);
      toast.error("An unexpected error occurred. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white">
      <div className="flex flex-col flex-1">
        <Header />
        <main className="flex-1 overflow-auto">
          {/* Filter Drawer */}
          <AnimatePresence>
            {filterDrawerOpen && (
              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ type: 'tween' }}
                className="fixed inset-y-0 left-0 z-50 w-80 bg-white dark:bg-gray-800 shadow-lg overflow-y-auto"
              >
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-medium">Filter Plans</h2>
                    <button
                      onClick={() => setFilterDrawerOpen(false)}
                      className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <XMarkIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    {/* Search */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Search</label>
                      <input
                        type="text"
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700"
                        placeholder="Search by plan name or ID"
                      />
                    </div>

                    {/* Status */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                      <Menu as="div" className="relative">
                        <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-left text-sm focus:outline-none">
                          <span>{filters.status || 'All'}</span>
                          <ChevronDownIcon className="h-4 w-4" />
                        </Menu.Button>
                        <Menu.Items className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {statusOptions.map((option) => (
                            <Menu.Item key={option}>
                              {({ active }) => (
                                <button
                                  onClick={() => handleFilterChange('status', option)}
                                  className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                                >
                                  {option}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </Menu.Items>
                      </Menu>
                    </div>

                    {/* Type */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Type</label>
                      <Menu as="div" className="relative">
                        <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-left text-sm focus:outline-none">
                          <span>{filters.type || 'All'}</span>
                          <ChevronDownIcon className="h-4 w-4" />
                        </Menu.Button>
                        <Menu.Items className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {typeOptions.map((option) => (
                            <Menu.Item key={option}>
                              {({ active }) => (
                                <button
                                  onClick={() => handleFilterChange('type', option)}
                                  className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                                >
                                  {option}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </Menu.Items>
                      </Menu>
                    </div>
                  </div>

                  <div className="mt-6 flex space-x-3">
                    <button
                      onClick={resetFilters}
                      className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                      Reset
                    </button>
                    <button
                      onClick={applyFilters}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Overlay when filter drawer is open */}
          {filterDrawerOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={() => setFilterDrawerOpen(false)}
            />
          )}

          <main className="flex-1 p-6 overflow-auto">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-600"
                >
                  <ChevronLeftIcon className="h-5 w-5 mr-1" />
                  Back
                </button>
                <h2 className="text-xl font-semibold">
                  Plan Management
                </h2>
                <div className='flex flex-row gap-3'>
                  <button
                    onClick={() => openPlanModal(null, true)}
                    className="px-3 py-2 text-sm text-white bg-green-600 rounded hover:bg-green-700 flex items-center"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add Plan
                  </button>
                  <button
                    onClick={() => setFilterDrawerOpen(true)}
                    className="p-2 rounded-full hover:bg-gray-300 bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center"
                  >
                    <TbFilterPause className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                  </button>
                </div>
              </div>
              {children || (
                <>
                  {/* Plans Cards */}
                  <div className="mb-6">
                    {loading && (
                      <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
                      </div>
                    )}

                    {!loading && (
                      <>
                        {getPaginatedData().length > 0 ? (
                          <div className="overflow-x-auto rounded-sm">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                              <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-xs uppercase">
                                <tr>
                                  <th className="px-3 py-2 text-left font-medium">Plan ID</th>
                                  <th className="px-3 py-2 text-left font-medium">Plan Name</th>
                                  <th className="px-3 py-2 text-center font-medium">Status</th>
                                  <th className="px-3 py-2 text-center font-medium">Type</th>
                                  <th className="px-3 py-2 text-center font-medium">Serial No.</th>
                                  <th className="px-3 py-2 text-center font-medium">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {getPaginatedData().map((plan) => (
                                  <tr key={plan.plan_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">

                                    {/* Plan ID */}
                                    <td className="px-3 py-2 align-middle font-medium text-gray-900 dark:text-white">
                                      {plan.plan_id}
                                    </td>

                                    {/* Plan Name */}
                                    <td className="px-3 py-2 align-middle text-gray-700 dark:text-gray-200">
                                      {plan.plan_name}
                                    </td>

                                    {/* Status */}
                                    <td className="px-3 py-2 align-middle text-center">
                                      <div className="flex items-center justify-center gap-2">
                                        <Switch
                                          checked={plan.status === "1"}
                                          onChange={(enabled) => handleStatusChange(plan.plan_id, enabled)}
                                          className={`${plan.status === "1" ? "bg-green-500" : "bg-red-500"
                                            } relative inline-flex h-5 w-9 items-center rounded-full`}
                                        >
                                          <span className="sr-only">Toggle status</span>
                                          <span
                                            className={`${plan.status === "1" ? "translate-x-5" : "translate-x-1"
                                              } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                                          />
                                        </Switch>
                                        <span
                                          className={`text-xs ${plan.status === "1"
                                            ? "text-green-600 dark:text-green-400"
                                            : "text-red-600 dark:text-red-400"
                                            }`}
                                        >
                                        </span>
                                      </div>
                                    </td>

                                    {/* Type */}
                                    <td className="px-3 py-2 align-middle text-center">
                                      <span
                                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold 
                ${plan.type === "default"
                                            ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                                            : "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                                          }`}
                                      >
                                        {plan.type || "custom"}
                                      </span>
                                    </td>

                                    {/* Serial No */}
                                    <td className="px-3 py-2 align-middle text-center text-gray-700 dark:text-gray-200">
                                      {plan.sl}
                                    </td>

                                    {/* Actions */}
                                    <td className="px-3 py-2 align-middle text-center">
                                      <div className="flex justify-center gap-2">
                                        <button
                                          onClick={() =>
                                            window.open(`/plan-slap?planId=${plan.plan_id}`, "_blank")
                                          }
                                          className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
                                          title="View Margin"
                                        >
                                          <EyeIcon className="h-5 w-5 text-blue-500" />
                                        </button>

                                        {!plan.type && (
                                          <>
                                            <button
                                              onClick={() => openPlanModal(plan.plan_id)}
                                              className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
                                              title="Edit Plan"
                                            >
                                              <PencilSquareIcon className="h-5 w-5 text-yellow-500" />
                                            </button>

                                            <button
                                              onClick={() => deletePlan(plan.plan_id)}
                                              className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
                                              title="Delete Plan"
                                            >
                                              <TrashIcon className="h-5 w-5 text-red-500" />
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                            No plans found
                          </div>
                        )}

                        {/* Pagination */}
                        <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                          <div className="flex-1 flex justify-between sm:hidden">
                            <button
                              onClick={() => handlePageChange(pagination.currentPage - 1)}
                              disabled={pagination.currentPage === 1}
                              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600"
                            >
                              Previous
                            </button>
                            <button
                              onClick={() => handlePageChange(pagination.currentPage + 1)}
                              disabled={pagination.currentPage === pagination.totalPages}
                              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600"
                            >
                              Next
                            </button>
                          </div>
                          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                              <p className="text-sm text-gray-700 dark:text-gray-300">
                                Showing <span className="font-medium">{(pagination.currentPage - 1) * pagination.itemsPerPage + 1}</span> to{' '}
                                <span className="font-medium">
                                  {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
                                </span>{' '}
                                of <span className="font-medium">{pagination.totalItems}</span> results
                              </p>
                            </div>
                            <div>
                              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                <button
                                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                                  disabled={pagination.currentPage === 1}
                                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                                >
                                  <span className="sr-only">Previous</span>
                                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                                </button>

                                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                  let pageNum;
                                  if (pagination.totalPages <= 5) {
                                    pageNum = i + 1;
                                  } else if (pagination.currentPage <= 3) {
                                    pageNum = i + 1;
                                  } else if (pagination.currentPage >= pagination.totalPages - 2) {
                                    pageNum = pagination.totalPages - 4 + i;
                                  } else {
                                    pageNum = pagination.currentPage - 2 + i;
                                  }

                                  return (
                                    <button
                                      key={pageNum}
                                      onClick={() => handlePageChange(pageNum)}
                                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${pagination.currentPage === pageNum
                                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600 dark:bg-gray-600 dark:text-white'
                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300'
                                        }`}
                                    >
                                      {pageNum}
                                    </button>
                                  );
                                })}

                                <button
                                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                                  disabled={pagination.currentPage === pagination.totalPages}
                                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                                >
                                  <span className="sr-only">Next</span>
                                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                                </button>
                              </nav>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </main>
        </main>
      </div>
      {/* Plan Modal */}
      <AnimatePresence>
        {isPlanModalOpen && (
          <>
            {/* Blurred Background Overlay */}
            <motion.div
              className="fixed inset-0 z-40 bg-black bg-opacity-30 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closePlanModal}
            />
            <motion.div
              className="fixed inset-0 z-50 overflow-y-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="flex items-center justify-center min-h-screen px-4 text-center sm:block sm:p-0">
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                  &#8203;
                </span>

                <motion.div
                  className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full relative z-50"
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                >
                  <div className="bg-white dark:bg-gray-800 pb-4 flex flex-col" style={{ maxHeight: '90vh' }}>


                    <div className="flex justify-between items-center border-b dark:border-gray-700 p-4 sticky top-0 bg-white dark:bg-gray-800 z-10 mb-2">
                      <h3 className="text-lg font-semibold dark:text-white">{isAddNew ? 'Add New Plan' : `Plan Details - ${selectedPlan?.plan_name}`}</h3>
                      <div
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50"
                      >
                        {!isAddNew && !isEditing && (
                          <button
                            onClick={() => setIsEditing(true)}
                            className="px-3 py-1 text-sm text-white bg-yellow-500 rounded hover:bg-yellow-600 flex items-center"
                          >
                            Edit
                          </button>
                        )}
                        <button
                          className="text-gray-600 hover:text-gray-500"
                          onClick={closePlanModal}
                        >
                          <XMarkIcon className="h-6 w-6" />
                        </button>
                      </div>
                    </div>

                    {/* Scrollable Content */}
                    <div className="overflow-y-auto pr-2 -mr-2 flex-grow">
                      <div className="grid grid-cols-1 gap-4 pb-2 px-4">
                        {/* Plan ID */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Plan ID
                          </label>
                          <div className="text-sm text-gray-900 dark:text-gray-200 py-2 px-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                            {isAddNew ? 'Auto-generated' : selectedPlan?.plan_id || 'N/A'}
                          </div>
                        </div>

                        {/* Plan Name */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Plan Name
                          </label>
                          {isEditing || isAddNew ? (
                            <input
                              value={editedPlan.plan_name || ''}
                              onChange={(e) => handleFieldChange('plan_name', e.target.value)}
                              className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                            />
                          ) : (
                            <div className="text-sm text-gray-900 dark:text-gray-200 py-2 px-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                              {selectedPlan?.plan_name || 'N/A'}
                            </div>
                          )}
                        </div>

                        {/* Status */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Status
                          </label>
                          {isEditing || isAddNew ? (
                            <select
                              value={editedPlan.status || '1'}
                              onChange={(e) => handleFieldChange('status', e.target.value)}
                              className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="1">Active</option>
                              <option value="0">Inactive</option>
                            </select>
                          ) : (
                            <div className="text-sm text-gray-900 dark:text-gray-200 py-2 px-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                              {selectedPlan?.status === '1' ? 'Active' : 'Inactive'}
                            </div>
                          )}
                        </div>

                        {/* Type */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Type
                          </label>
                          <div className="text-sm text-gray-900 dark:text-gray-200 py-2 px-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                            {isAddNew ? 'Custom' : selectedPlan?.type || 'custom'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Footer with action buttons */}
                    <div className="mt-4 pt-4 flex justify-end gap-3">
                      {(isEditing || isAddNew) ? (
                        <div className='px-4 gap-4 flex'>
                          <button
                            onClick={closePlanModal}
                            className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSavePlan}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                          >
                            {isAddNew ? 'Add Plan' : 'Save Changes'}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <ToastContainer />
    </div>
  );
};

export default Plans;